import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi, cmsApi } from '../lib/api';
import { 
  Settings, ArrowLeft, Save, Loader2, Globe, Mail, Phone, MapPin
} from 'lucide-react';
import { FadeIn, AnimatedCard } from '../components/ui/AnimatedCard';
import toast from 'react-hot-toast';

interface SettingItem {
  key: string;
  value: string;
}

export function AdminSettings() {
  const queryClient = useQueryClient();

  const { data: settingsData, isLoading } = useQuery({
    queryKey: ['admin-settings'],
    queryFn: async () => {
      const res = await cmsApi.getSettings();
      // API returns { key: value } object, convert to array
      const obj = res.data as Record<string, string>;
      return Object.entries(obj).map(([key, value]) => ({ key, value })) as SettingItem[];
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ key, value }: { key: string; value: string }) => 
      adminApi.updateSetting(key, value),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-settings'] });
      toast.success('Setting updated');
    },
    onError: () => toast.error('Failed to update setting'),
  });

  const allSettings = settingsData || [];

  const handleUpdate = (key: string, value: string) => {
    updateMutation.mutate({ key, value });
  };

  const settingGroups = [
    {
      title: 'Site Information',
      icon: Globe,
      keys: ['site_name', 'site_tagline', 'site_description'],
    },
    {
      title: 'Contact Information',
      icon: Mail,
      keys: ['contact_email', 'contact_phone', 'contact_address'],
    },
    {
      title: 'Social Media',
      icon: Globe,
      keys: ['facebook_url', 'youtube_url', 'telegram_url'],
    },
  ];

  const getSettingValue = (key: string) => {
    const setting = allSettings.find((s: SettingItem) => s.key === key);
    return setting?.value || '';
  };

  const getSettingLabel = (key: string) => {
    return key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };

  return (
    <div className="space-y-6">
      <FadeIn>
        <div className="flex items-center gap-4">
          <Link to="/admin" className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
            <ArrowLeft className="w-5 h-5 text-slate-600" />
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Site Settings</h1>
            <p className="text-slate-500">Manage website configuration</p>
          </div>
        </div>
      </FadeIn>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
        </div>
      ) : (
        <div className="space-y-6">
          {settingGroups.map((group) => (
            <FadeIn key={group.title}>
              <AnimatedCard className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-primary-100 text-primary-600 rounded-xl flex items-center justify-center">
                    <group.icon className="w-5 h-5" />
                  </div>
                  <h2 className="text-lg font-bold text-slate-900">{group.title}</h2>
                </div>

                <div className="space-y-4">
                  {group.keys.map((key) => (
                    <SettingField
                      key={key}
                      label={getSettingLabel(key)}
                      value={getSettingValue(key)}
                      onUpdate={(value) => handleUpdate(key, value)}
                      isLoading={updateMutation.isPending && updateMutation.variables?.key === key}
                    />
                  ))}
                </div>
              </AnimatedCard>
            </FadeIn>
          ))}
        </div>
      )}
    </div>
  );
}

function SettingField({ label, value, onUpdate, isLoading }: {
  label: string;
  value: string;
  onUpdate: (value: string) => void;
  isLoading: boolean;
}) {
  const [editValue, setEditValue] = useState(value);
  const [isEditing, setIsEditing] = useState(false);

  const handleSave = () => {
    onUpdate(editValue);
    setIsEditing(false);
  };

  return (
    <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl">
      <div className="flex-1">
        <div className="text-sm font-medium text-slate-700 mb-1">{label}</div>
        {isEditing ? (
          <input
            type="text"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none text-sm"
            autoFocus
          />
        ) : (
          <div className="text-sm text-slate-600">{value || <span className="text-slate-400 italic">Not set</span>}</div>
        )}
      </div>
      <div className="flex items-center gap-2">
        {isEditing ? (
          <>
            <button
              onClick={handleSave}
              disabled={isLoading}
              className="flex items-center gap-1 px-3 py-1.5 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
            >
              {isLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
              Save
            </button>
            <button
              onClick={() => { setEditValue(value); setIsEditing(false); }}
              className="px-3 py-1.5 text-sm text-slate-500 hover:bg-slate-200 rounded-lg transition-colors"
            >
              Cancel
            </button>
          </>
        ) : (
          <button
            onClick={() => setIsEditing(true)}
            className="px-3 py-1.5 text-sm text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
          >
            Edit
          </button>
        )}
      </div>
    </div>
  );
}
